import { useTranslation } from '../../../../../shared/i18n';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Select, Space, Table, Tag, Tooltip, Typography, message } from 'antd';
import { GiftOutlined } from '@ant-design/icons';
import apiClient from '../../../../../shared/services/apiClient';
import { formatDate } from '../../../../../shared/utils/date';
import { userPath } from '../../../../../shared/config/paths';

const { Text } = Typography;

const KIND = { dns: 'DNS', cdn: 'CDN', yeapmail: 'Email', ssl: 'SSL', evm: 'Cloud server', host: 'Dedicated hosting', host_shared: 'Web hosting' };
const POLICY = { free: 'Free renewal', coupon: 'Discounted renewal', list: 'Renews at list price' };
const STATUS = {
    pending: { color: 'default', text: 'Pending activation' },
    voucher: { color: 'gold', text: 'Unclaimed' },
    active: { color: 'success', text: 'Active' },
    ended: { color: 'default', text: 'Ended' },
    void: { color: 'default', text: 'Voided' },
    converted: { color: 'blue', text: 'Converted to paid' },
};

/** 赠品升级入口：去对应资源的套餐页（付款后转为独立付费订阅）。 */
const upgradePath = (g) => {
    const id = g.resource_id;
    if (g.product_type_code === 'cdn' && id) return `/cdn/plans?domain_id=${id}`;
    if (g.product_type_code === 'dns' && id) return `/dns/plans?zone_id=${id}`;
    if (g.product_type_code === 'yeapmail' && id) return `/webmail/plans?domain_id=${id}`;
    return '/billing/subscription';
};

/** 域名注册赠品（清单 §4）：随域名到期/续费；待领取的在这里领取。没有赠品时不显示。 */
export default function DomainGifts({ domainId }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [pick, setPick] = useState({});
    const [busy, setBusy] = useState(null);

    const load = useCallback(async () => {
        try {
            const r = (await apiClient.get('/api/user/domain-registration/gifts', { params: { id: domainId } })).data;
            setRows(r?.success && Array.isArray(r.data) ? r.data : []);
        } catch (e) {
            setRows([]);
        }
    }, [domainId]);
    useEffect(() => { if (domainId) load(); }, [domainId, load]);

    const redeem = async (g) => {
        setBusy(g.id);
        try {
            const r = (await apiClient.post('/api/user/domain-registration/gifts/redeem', { grant_id: g.id, resource_id: pick[g.id] })).data;
            if (r?.success && r.data?.checkout) {
                navigate(userPath(`/provision/${r.data.product_type}?gift_grant_id=${r.data.gift_grant_id}&product_id=${r.data.product_id}&billing_cycle=${r.data.billing_cycle}`));
                return;
            }
            if (r?.success) { message.success(t('Claimed')); load(); } else message.error(r?.error || t('Couldn\'t claim'));
        } catch (e) {
            message.error(e?.response?.data?.error || e?.error || e?.message || t('Couldn\'t claim'));
        } finally {
            setBusy(null);
        }
    };

    if (!rows.length) return null;

    const columns = [
        {
            title: t('Gift'), key: 'p', render: (_, g) => (
                <Space size={4} wrap>
                    <Tag>{t(KIND[g.product_type_code] || g.product_type_code)}</Tag>
                    <span>{g.product_name}</span>
                    {g.resource_name && <Text type="secondary">· {g.resource_name}</Text>}
                </Space>
            ),
        },
        { title: t('Status'), dataIndex: 'status', width: 90, render: (s) => <Tag color={STATUS[s]?.color}>{t(STATUS[s]?.text || s)}</Tag> },
        {
            title: <Tooltip title={t('Ends with the domain; renews this way when the domain renews')}>{t('Expires')}</Tooltip>, key: 'e', width: 150,
            render: (_, g) => (g.included_free ? <Text type="secondary">{t('Free for life')}</Text> : (
                <Space direction="vertical" size={0}>
                    <span>{formatDate(g.subscription_expiry || g.covered_until)}</span>
                    <Text type="secondary" style={{ fontSize: 12 }}>{t(POLICY[g.renewal_policy] || '')}</Text>
                </Space>
            )),
        },
        {
            title: '', key: 'a', width: 260, render: (_, g) => {
                if (g.upgradable) {
                    return (
                        <Tooltip title={t('After payment it becomes a standalone paid subscription that renews at the new plan')}>
                            <Button size="small" onClick={() => navigate(userPath(upgradePath(g)))}>{t('Upgrade')}</Button>
                        </Tooltip>
                    );
                }
                if (g.status !== 'voucher') return null;
                const canPick = (g.candidates || []).length > 0;
                if (g.redeem === 'attach' || (g.redeem === 'attach_or_checkout' && canPick)) {
                    return canPick ? (
                        <Space size={4}>
                            <Select size="small" style={{ width: 160 }} placeholder={t('Select a resource')} value={pick[g.id]}
                                onChange={(v) => setPick((p) => ({ ...p, [g.id]: v }))}
                                options={g.candidates.map((c) => ({ value: c.id, label: c.name }))} />
                            <Button size="small" type="primary" disabled={!pick[g.id]} loading={busy === g.id} onClick={() => redeem(g)}>{t('Claim')}</Button>
                        </Space>
                    ) : <Text type="secondary">{t('Add this domain\'s {{kind}} first, then come back to claim', { kind: t(KIND[g.product_type_code] || '') })}</Text>;
                }
                if (g.product_type_code === 'ssl') {
                    return <Text type="secondary">{t('Contact support to claim')}</Text>;
                }
                return <Button size="small" type="primary" loading={busy === g.id} onClick={() => redeem(g)}>{t('Set up')}</Button>;
            },
        },
    ];

    return (
        <Card size="small" title={<Space><GiftOutlined />{t('Registration gifts')}</Space>}>
            <Table size="small" rowKey="id" pagination={false} dataSource={rows} columns={columns} scroll={{ x: true }} />
        </Card>
    );
}
